import { gql } from '@apollo/client';

/**
 * One page of the contact list (CRM_CONTACT_QUERY_FRONTEND.md §1–§3, §7).
 *
 * - Scalar columns are always selected: they cost nothing extra. Each related
 *   list (`tags`, `owner`) is one more query per page on the server, so it is
 *   asked only while its column is shown.
 * - `total` is asked once per filter, on the first page requested for it, then
 *   reused: paging and sorting do not change it.
 * - `matchedOn` is null without a search. The screen shows it only while
 *   searching (see CrmContacts.tsx for why).
 */
export const CRM_CONTACT_LIST = gql`
  query CrmContactList(
    $filter: CrmContactFilter
    $sort: [CrmContactSort!]
    $take: Int
    $skip: Int
    $after: String
    $withTotal: Boolean!
    $withTags: Boolean!
    $withOwner: Boolean!
    $withEngagement: Boolean!
  ) {
    crmContacts(filter: $filter, sort: $sort, take: $take, skip: $skip, after: $after) {
      items {
        id
        fullName
        primaryEmail
        primaryPhone
        city
        region
        countryCode
        canEmail
        canCall
        canText
        doNotCall
        doNotContact
        callCount
        lastCalledAt
        lastContactedAt
        supportLevel
        lifecycleStage
        source
        nbCreatedAt
        createdAt
        joinedAt
        matchedOn
        tags @include(if: $withTags) {
          tagId
          name
          color
        }
        owner @include(if: $withOwner) {
          id
          firstName
          lastName
        }
        engagement @include(if: $withEngagement) {
          emailsSent
          engagementRate
        }
      }
      skip
      take
      hasNextPage
      hasPreviousPage
      endCursor
      total @include(if: $withTotal) {
        count
        exact
        exactLimit
      }
    }
  }
`;

/**
 * Exact counts and the consent breakdown for the current filter (§10). Keyed by
 * the filter alone, so paging and sorting read it from the cache.
 */
export const CRM_CONTACT_CONSENT_COUNTS = gql`
  query CrmContactConsentCounts($filter: CrmContactFilter) {
    crmContactCounts(filter: $filter) {
      total
      canEmail
      canCall
      canText
      doNotCall
      doNotContact
    }
  }
`;

/**
 * What the tag, list and country filters pick from, asked once when the screen
 * opens and then cached (§11–§13, §16). Counts are across the whole CRM, not the
 * current filter. Asked up front because the pills name chosen tags and lists.
 *
 * The rate limit counts every root field, so this request is 3 of the admin's
 * 120 a minute. The other filters' choices are one small request each, sent the
 * first time that filter's panel opens. `crmSegments` returns at most 200: the
 * largest lists. More are found by name with CRM_CONTACT_LIST_SEARCH.
 */
export const CRM_CONTACT_FILTER_OPTIONS = gql`
  query CrmContactFilterOptions {
    crmTags {
      id
      name
      color
      isSystem
      contactCount
    }
    countries: crmContactFacet(field: COUNTRY, take: 500) {
      value
      count
    }
    lists: crmSegments(type: STATIC, take: 200) {
      total
      items {
        id
        name
        contactCount
      }
    }
  }
`;

/*
 * The other filters' choices, each counted across the whole CRM: one root field
 * (one unit of the rate limit) each, sent the first time that filter's panel
 * opens and then cached for the visit. Point people are also asked when a link
 * names some, so their pill can show the names.
 */

export const CRM_CONTACT_OWNER_OPTIONS = gql`
  query CrmContactOwnerOptions {
    owners: crmContactFacet(field: OWNER, take: 200) {
      value
      label
      count
    }
  }
`;

export const CRM_CONTACT_SOURCE_OPTIONS = gql`
  query CrmContactSourceOptions {
    sources: crmContactFacet(field: SOURCE) {
      value
      count
    }
  }
`;

export const CRM_CONTACT_STAGE_OPTIONS = gql`
  query CrmContactStageOptions {
    stages: crmContactFacet(field: LIFECYCLE_STAGE, take: 200) {
      value
      count
    }
  }
`;

export const CRM_CONTACT_SUPPORT_OPTIONS = gql`
  query CrmContactSupportOptions {
    supportLevels: crmContactFacet(field: SUPPORT_LEVEL) {
      value
      count
    }
  }
`;

/**
 * Chosen lists by id, for naming the chips of lists outside the first 200
 * (a filter restored from a link). `ids` is card 18's API addition.
 */
export const CRM_CONTACT_LIST_NAMES = gql`
  query CrmContactListNames($ids: [Int!]!) {
    crmSegments(ids: $ids, take: 200) {
      items {
        id
        name
        contactCount
      }
    }
  }
`;

/** Lists by name, for the list filter when there are more than the first request held. */
export const CRM_CONTACT_LIST_SEARCH = gql`
  query CrmContactListSearch($search: String!) {
    crmSegments(search: $search, type: STATIC, take: 20) {
      items {
        id
        name
        contactCount
      }
    }
  }
`;

/**
 * The search box's suggestions (§4.1): the same matching as the list's search,
 * best first. Sent with `fetchPolicy: 'no-cache'`, so its `matchedOn` never
 * lands on the cached contacts the list is showing.
 */
export const CRM_CONTACT_QUICK_SEARCH = gql`
  query CrmContactQuickSearch($text: String!) {
    crmContactSearch(text: $text, take: 8) {
      hasMore
      items {
        id
        fullName
        primaryEmail
        primaryPhone
        city
        countryCode
        matchedOn
        doNotCall
        doNotContact
      }
    }
  }
`;
